import { useEffect, useRef, useState } from 'react';
import { BrandMark, IconHistory, IconStudio, IconWallet } from './Icons';
import { formatNumber } from '../lib/format';

export type ViewId = 'studio' | 'credits' | 'history';

/**
 * `mock`    — deliberately simulated (VITE_ENGINE=mock)
 * `live`    — real engine, reachable
 * `offline` — real engine selected but not responding
 * `probing` — still discovering
 */
export type EngineMode = 'mock' | 'live' | 'offline' | 'probing';

interface TopBarProps {
  view: ViewId;
  onChange: (view: ViewId) => void;
  credits: number;
  historyCount: number;
  engineLabel: string;
  engineMode: EngineMode;
}

const ENGINE_UI: Record<EngineMode, { text: string; title: string }> = {
  mock: {
    text: 'Mock engine',
    title:
      'Simulated mode: nothing is really generated. Remove VITE_ENGINE=mock from .env to go live.',
  },
  live: {
    text: '',
    title: 'The real engine is connected — Generate produces a real video.',
  },
  offline: {
    text: 'Engine offline',
    title:
      'The engine is not responding. Start Maestro, then this badge turns green on its own. ' +
      'If it runs on an unusual port, set VITE_MAESTRO_BASE_URL in .env',
  },
  probing: { text: 'Finding engine…', title: 'Connecting to the local engine…' },
};

const NAV: { id: ViewId; label: string; icon: typeof IconStudio }[] = [
  { id: 'studio', label: 'Studio', icon: IconStudio },
  { id: 'credits', label: 'Credits', icon: IconWallet },
  { id: 'history', label: 'History', icon: IconHistory },
];

export function TopBar({
  view,
  onChange,
  credits,
  historyCount,
  engineLabel,
  engineMode,
}: TopBarProps) {
  const [flash, setFlash] = useState(false);
  const previous = useRef(credits);

  useEffect(() => {
    if (previous.current !== credits) {
      previous.current = credits;
      setFlash(true);
      const id = window.setTimeout(() => setFlash(false), 900);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [credits]);

  return (
    <header className="topbar">
      <div className="wrap topbar-inner">
        <div className="brand">
          <BrandMark className="brand-mark" />
          <span className="brand-text">
            <span className="brand-name">Hunyadi</span>
            <span className="brand-sub">Hungarian video generation app</span>
          </span>
        </div>

        <nav className="nav" aria-label="Main navigation">
          {NAV.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              className="nav-btn"
              aria-current={view === id ? 'page' : undefined}
              onClick={() => onChange(id)}
            >
              <Icon size={15} />
              {label}
              {id === 'history' && historyCount > 0 && (
                <span className="badge mono">{historyCount}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="topbar-right">
          {/*
            Engine status. Three distinct states so the badge never lies:
            deliberately mocked / real engine live / real engine unreachable.
          */}
          <span
            className={`engine-pill ${engineMode === 'live' ? 'is-live' : ''}`}
            title={ENGINE_UI[engineMode].title}
          >
            <span className="engine-dot" />
            <span className="engine-label">
              {ENGINE_UI[engineMode].text || engineLabel}
            </span>
          </span>

          <button
            type="button"
            className={`credit-pill ${flash ? 'is-flashing' : ''}`}
            onClick={() => onChange('credits')}
            title="Credit balance — click to top up"
          >
            <span className="value">{formatNumber(credits)}</span>
            <span className="unit">credits</span>
          </button>
        </div>
      </div>
    </header>
  );
}
